import { useState, useEffect, useCallback } from 'react'
import type { FoundryAsset, AssetType, GenerateRequest } from '../types'

export function useFoundry() {
  const [assets, setAssets] = useState<FoundryAsset[]>([])
  const [loading, setLoading] = useState(true)
  const [generating, setGenerating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [filterType, setFilterType] = useState<AssetType | null>(null)
  const [filterTags, setFilterTags] = useState<string[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [hasApiKey, setHasApiKey] = useState(false)

  useEffect(() => {
    fetch('/api/foundry/config')
      .then((r) => r.json())
      .then((cfg) => setHasApiKey(cfg.hasApiKey))
      .catch(() => {})
  }, [])

  const fetchAssets = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams()
      if (filterType) params.set('type', filterType)
      if (filterTags.length) params.set('tags', filterTags.join(','))
      if (searchQuery.trim()) params.set('search', searchQuery.trim())
      params.set('limit', '100')

      const res = await fetch(`/api/foundry/assets?${params}`)
      const data = await res.json()
      if (Array.isArray(data)) {
        setAssets(data)
      } else if (data.error) {
        setError(data.error)
      }
    } catch (err: any) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [filterType, filterTags, searchQuery])

  useEffect(() => {
    fetchAssets()
  }, [fetchAssets])

  const generateAsset = useCallback(async (req: GenerateRequest): Promise<FoundryAsset | FoundryAsset[] | null> => {
    setGenerating(true)
    setError(null)
    try {
      const res = await fetch('/api/foundry/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(req),
      })
      const data = await res.json()
      if (data.error) {
        setError(data.error)
        return null
      }
      await fetchAssets()
      return data
    } catch (err: any) {
      setError(err.message)
      return null
    } finally {
      setGenerating(false)
    }
  }, [fetchAssets])

  const captureFrame = useCallback(async (opts: {
    type: AssetType
    name: string
    tags: string[]
    imageBase64: string
    mimeType?: string
    sourceVideoId?: string
    timestampSec?: number
  }): Promise<FoundryAsset | null> => {
    setError(null)
    try {
      const res = await fetch('/api/foundry/capture', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(opts),
      })
      const data = await res.json()
      if (data.error) {
        setError(data.error)
        return null
      }
      await fetchAssets()
      return data
    } catch (err: any) {
      setError(err.message)
      return null
    }
  }, [fetchAssets])

  const updateAsset = useCallback(async (id: string, updates: { name?: string; tags?: string[] }) => {
    setError(null)
    try {
      const res = await fetch(`/api/foundry/assets/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      })
      const data = await res.json()
      if (data.error) {
        setError(data.error)
        return
      }
      setAssets((prev) => prev.map((a) => (a.id === id ? { ...a, ...data } : a)))
    } catch (err: any) {
      setError(err.message)
    }
  }, [])

  const deleteAsset = useCallback(async (id: string) => {
    setError(null)
    try {
      await fetch(`/api/foundry/assets/${id}`, { method: 'DELETE' })
      setAssets((prev) => prev.filter((a) => a.id !== id))
    } catch (err: any) {
      setError(err.message)
    }
  }, [])

  const dismissError = useCallback(() => setError(null), [])

  return {
    assets,
    loading,
    generating,
    error,
    hasApiKey,
    filterType,
    setFilterType,
    filterTags,
    setFilterTags,
    searchQuery,
    setSearchQuery,
    generateAsset,
    captureFrame,
    updateAsset,
    deleteAsset,
    dismissError,
    refresh: fetchAssets,
  }
}
